import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, TextField, Button, useTheme, Alert, CircularProgress, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { whatsAppHref } from '../constants/conversion';

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '', address: '' });

  useEffect(() => {
    apiService.getFooter()
      .then((data) => {
        if (data && data.footer) {
          const f = data.footer;
          setContactInfo({
            email: f.email || 'info@boldcompsolutions.com',
            phone: f.phone || '0726642205',
            address: f.address || 'P.O. Box 85573–00200, Nairobi, Kenya'
          });
        }
      })
      .catch(() => {
        setContactInfo({
          email: 'info@boldcompsolutions.com',
          phone: '0726642205',
          address: 'P.O. Box 85573–00200, Nairobi, Kenya'
        });
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert({ show: false });
    setLoading(true);
    try {
      await apiService.submitContact({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });
      setAlert({ show: true, type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to send message. Please try again.';
      setAlert({ show: true, type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}>
          <Typography variant="h2" textAlign="center" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 800 }}>
            Talk to a human — fast
          </Typography>
          <Typography variant="h5" textAlign="center" sx={{ mb: 2, color: 'text.secondary', maxWidth: 640, mx: 'auto', lineHeight: 1.6 }}>
            Projects, courses, or partnerships: send a message or WhatsApp. We prioritise serious enquiries and usually
            reply the same day.
          </Typography>
        </motion.div>

        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
                Direct lines
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>📧 Email</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{contactInfo.email}</Typography>
                
                <Typography variant="h6" gutterBottom>📞 Phone</Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{contactInfo.phone}</Typography>
                
                <Typography variant="h6" gutterBottom>📍 Address</Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>{contactInfo.address}</Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                  <Button
                    component="a"
                    href={whatsAppHref('Hi Boldcomp — I want to talk about a project or course.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="success"
                    size="large"
                    sx={{ fontWeight: 700 }}
                  >
                    WhatsApp us
                  </Button>
                  <Button component={Link} to="/request-quote" variant="outlined" color="primary" size="large" sx={{ fontWeight: 700 }}>
                    Request project scope
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ width: '100%', height: 300, bgcolor: 'grey.100', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Google Maps Embed - Nairobi, Kenya
                </Typography>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                {alert.show && (
                  <Alert severity={alert.type} sx={{ mb: 2 }}>
                    {alert.message}
                  </Alert>
                )}
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField required fullWidth label="Your Name" name="name" value={formData.name} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField required fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required fullWidth label="Subject" name="subject" value={formData.subject} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField required fullWidth label="Message" name="message" multiline rows={4} value={formData.message} onChange={handleChange} />
                  </Grid>
                </Grid>
                
                <Button type="submit" variant="contained" color="secondary" size="large" sx={{ mt: 3, px: 4, fontWeight: 700 }} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Send message — we will reply'}
                </Button>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;